import React, { useState, useEffect, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useDispatch, useSelector } from "react-redux";

import { PredictionChartButton } from "@/components/dataPages";
import { NoData, TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { ErrorComponent } from "@/components/common/errors";
// import { Skeleton } from "@/components/common/other/Skeleton";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { clearLoadVolumeState, getLoadVolume } from "@/state/loadVolumeSlice";
import { useQuery } from "@/utils/query";
import { cn } from "@/utils/css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export const PredictionBarChart = ({ withDelivery = false, title = "", tooltipText = "" }) => {
    const { data: volumeData, loading, error } = useSelector((state) => state.loadVolume);
    const dispatch = useDispatch();
    const chartRef = useRef(null);

    const isDarkTheme = useIsDarkTheme();

    const [loadVolumeInterval, setLoadVolumeInterval] = useState(90);
    const { pickupZip, deliveryZip, trailerType } = useQuery();

    const dateIntervals = [
        { name: "90d", value: 90 },
        { name: "1y", value: 365 },
    ];

    const dayIndex = new Date().getDay() - 1;
    const [maxValue, setMaxValue] = useState(0);
    const [chartData, setChartData] = useState({
        labels: ["M", "T", "W", "T", "F", "S", "S"],
        datasets: [
            {
                data: volumeData,
                backgroundColor: [],
                borderRadius: 4,
                datalabels: {
                    align: "start",
                    anchor: "end",
                    offset: -26,
                    color: isDarkTheme ? "#E0E2E6" : "#343735",
                    font: {
                        family: "Saira",
                        size: 14,
                        height: 18,
                    },
                    formatter: (value) => (maxValue ? `${value} %` : ""),
                },
                barThickness: 32,
            },
        ],
    });

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 26,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                enabled: false,
            },
            labels: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                display: false,
            },
            x: {
                beginAtZero: true,
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        family: "Saira",
                        size: 14,
                        height: 18,
                    },
                    color: isDarkTheme ? "#E0E2E6" : "#343735",
                },
                border: {
                    display: false,
                },
            },
        },
        animations: {
            colors: false,
        },
        animation: {
            duration: 750,
            easing: "easeOutQuart",
        },
    };

    useEffect(() => {
        dispatch(getLoadVolume({ pickupZip, deliveryZip: "", trailerType, loadVolumeInterval }));
    }, [loadVolumeInterval, pickupZip, deliveryZip, trailerType]);

    const calculateMaxValue = (data) => {
        return Math.max(...data);
    };

    useEffect(() => {
        setMaxValue(calculateMaxValue(volumeData));
    }, [volumeData]);

    useEffect(() => {
        setChartData({
            ...chartData,
            datasets: [
                {
                    ...chartData.datasets[0],
                    data: volumeData,
                    backgroundColor: volumeData.map((_, index) =>
                        dayIndex === index
                            ? isDarkTheme
                                ? "rgba(36, 185, 140, 0.6)"
                                : "rgba(67, 188, 152, 0.6)"
                            : isDarkTheme
                              ? "rgba(36, 185, 140, 0.1)"
                              : "rgba(67, 188, 152, 0.1)",
                    ),
                    datalabels: {
                        ...chartData.datasets[0].datalabels,
                        formatter: (value) => (maxValue ? `${value} %` : ""),
                    },
                },
            ],
        });
    }, [maxValue]);

    useEffect(() => {
        return () => {
            dispatch(clearLoadVolumeState());
        };
    }, []);

    return (
        <div className={"flex flex-col items-center"}>
            <div
                className={cn("flex items-center justify-between pb-3 w-full", {
                    "pb-5": !withDelivery,
                })}
            >
                <h2 className="text-cm-p-text-primary text-base/[22px] flex items-center">
                    <TranslatedText>{title}</TranslatedText>
                    <div className="ml-2">
                        <InfoButton text={tooltipText} />
                    </div>
                </h2>
                <div className="button-group text-cm-p-text-secondary text-sm border border-cm-p-outline-variation rounded-[58px]">
                    {dateIntervals.map((di) => (
                        <PredictionChartButton
                            option={di}
                            setActiveValue={setLoadVolumeInterval}
                            activeValue={loadVolumeInterval}
                            key={di.name}
                        />
                    ))}
                </div>
            </div>
            <div
                className={cn(
                    "flex flex-col flex-1 items-center justify-center relative w-full h-[230px] min-h-[230px] min-[1300px]:h-[179px] min-[1300px]:min-h-[179px]",
                    {
                        "h-[309px] min-h-[309px] min-[1300px]:h-[309px] min-[1300px]:min-h-[309px]": withDelivery,
                    },
                )}
            >
                <div
                    className={cn("w-full h-[230px] min-h-[230px] min-[1300px]:h-[179px] min-[1300px]:min-h-[179px]", {
                        "h-[309px] min-h-[309px] min-[1300px]:h-[309px] min-[1300px]:min-h-[309px]": withDelivery,
                    })}
                >
                    <Bar data={chartData} options={options} ref={chartRef} />
                </div>
                {loading || error || (volumeData?.length && maxValue !== 0) ? null : <NoData className="absolute top-20" />}
                {/*Commented out skeleton to discuss with team */}
                {/*<Skeleton*/}
                {/*    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0 opacity-50", {*/}
                {/*        hidden: !loading,*/}
                {/*    })}*/}
                {/*/>*/}
                <ErrorComponent
                    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0", {
                        hidden: !error,
                    })}
                />
            </div>
        </div>
    );
};
